/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from "@/shared/state";

import { produce } from "immer";
import { PropertyResponseType } from "@services";
import { v1 as uuid } from "uuid";

export type PropertyLoadError = Record<string, any> | null;

interface AgencyContextType {
  property: PropertyResponseType;
  editMode: boolean;
  fetchId: string;
  /** Whether the first detail fetch has resolved (success or failure). */
  loaded: boolean;
  /** Terminal error from the detail fetch; `null` while loading or on success. */
  error: PropertyLoadError;
  set: HandleChangeStore;
  get: () => AgencyContextType;
  setProperty: HandleChangeValues;
  refreshFetch: () => void;
  setEditMode: HandleChangeEditMode;
  setLoadState: (loaded: boolean, error: PropertyLoadError) => void;
}

export type HandleChangeStore = (
  partial:
    | AgencyContextType
    | Partial<AgencyContextType>
    | ((
        state: AgencyContextType
      ) => AgencyContextType | Partial<AgencyContextType>),
  replace?: boolean | undefined
) => void;

export type HandleChangeValues = (property: PropertyResponseType) => void;
export type HandleChangeEditMode = (value: boolean) => void;

export const propertyContext = createLocalZustand<AgencyContextType>(
  (initializer) => (set, get) => ({
    property: { ...initializer } as PropertyResponseType,
    editMode: false,
    fetchId: uuid(),
    loaded: false,
    error: null,
/**
 * setProperty function.
 *
 * @param {*} property - Parameter description
 *
 * @returns {*} Return value description
 */

    setProperty: (property) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.property = property;
          draft.loaded = true;
          draft.error = null;
        });
        return newState;
      }),
/**
 * refreshFetch function.
 *
 * @returns {*} Return value description
 */

    refreshFetch: () =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.fetchId = uuid();
          draft.loaded = false;
          draft.error = null;
        });
        return newState;
      }),
/**
 * setLoadState marks the detail fetch as resolved, recording any terminal error.
 *
 * @param {boolean} loaded - Whether the first fetch attempt has resolved.
 * @param {PropertyLoadError} error - The terminal error, or `null` on success.
 *
 * @returns {void}
 */

    setLoadState: (loaded, error) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.loaded = loaded;
          draft.error = error;
        });
        return newState;
      }),
/**
 * setEditMode function.
 *
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */

    setEditMode: (value) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.editMode = value;
        });
        return newState;
      }),

    set: set,
    get: get,
  })
);

export const { useStore: useProperty } = propertyContext;
